import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router, NavigationEnd, ActivationEnd, ActivatedRoute } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import mixpanel from "mixpanel-browser";
import { StorageKeyEnum } from "./core/StorageKeyEnum";
import { LoginModel } from "./models/login.model";
import { AuthService } from "./services/auth.service";
import { MainService } from "./services/main.service";
import { PrintService } from "./services/print.service";
import { HeaderService } from "./components/header/header.service";
import { RedirectUrls } from './core/Constants';
import { PermissionsEnum } from './core/PermissionsEnum';
import { InventoryService } from './services/inventory.service';
@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
  public title = "datavanced";
  public show:boolean = true;
  private destroy$ = new Subject<void>();
  isNavVisable: boolean;
  isNavCollapsed: boolean;
  parentRouteName: string;
  userInfo:LoginModel;

  constructor(
    public printService: PrintService,
    private titleService: Title,
    private authService: AuthService,
    public main: MainService,
    public router: Router,
    private snackbar: MatSnackBar,
    private headerService: HeaderService,
    private route: ActivatedRoute,
    private location: Location,
    private inventoryService: InventoryService
  ) {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof ActivationEnd) {
        this.parentRouteName = event.snapshot.url.length
          ? event.snapshot.url[0].path
          : "empty";
        if (
          this.parentRouteName === "account" ||
          this.parentRouteName === "login" ||
          this.parentRouteName === "onboarding" ||
          this.parentRouteName === "integrations"
        ) {
          this.isNavVisable = false;
          if(this.parentRouteName === "account" || this.parentRouteName === "integrations") this.setRouteByPermission(event);
        } else {
          this.setRouteByPermission(event)
          this.isNavVisable = true;
          this.logReport();
        }
        const index =
          this.getTitle(router.routerState, router.routerState.root).length - 1;
        const title = this.getTitle(
          router.routerState,
          router.routerState.root
        )[index];
        this.titleService.setTitle(`${title} | nineyard`);
        this.headerService.title.next(title);
      }
    });

    this.authService.userInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: LoginModel) => {   
        this.userInfo=  res   
        const pathArray = window.location.href.split( '/' );
        const url = pathArray[0] + '//' + pathArray[2];
        if(url.includes(RedirectUrls.AppUrl) && res && res?.isPlanYard == 2 && res.userCode){
          const code=res.userCode
          this.authService.logout();
          window.open(RedirectUrls.PlanyardUrl+"/account/login/"+code,'_self').close();
        }

        if (this.location.path() === '/' || this.location.path() === '') {
          this.authService.redirectRouteOnLoad(res, this.route.snapshot.queryParams.returnUrl);
        }
        this.setThirdPartyUsernames(res);
      });

    main.uiLoaderHideShow.pipe(takeUntil(this.destroy$)).subscribe((show) => {
      this.show = show;
    });

    router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (!this.show) {
          this.show = true;
        }
      }
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  ngOnInit(): void {
    this.checkForValidToken();
  }

  onActivate(event) {
    window.scroll(0, 0);
    if (
      document.getElementsByClassName("cdk-overlay-backdrop-showing").length !==
      0
    ) {
      const backdrop = document.getElementsByClassName(
        "cdk-overlay-backdrop-showing"
      )[0] as HTMLElement;
      backdrop.click();
    }
  }

  private getTitle(state: any, parent: any) {
    const data = [];
    if (parent && parent.snapshot.data && parent.snapshot.data.title) {
      data.push(parent.snapshot.data.title);
    }

    if (state && parent) {
      data.push(...this.getTitle(state, state.firstChild(parent)));
    }
    return data;
  }

  private checkForValidToken() {
    const userString = localStorage.getItem(StorageKeyEnum.User);
    if (userString) {
      const userObj = JSON.parse(userString);
      const dateNow = new Date();
      const loginExpireDate = new Date(userObj.tokenExpire);
      const isExprired = loginExpireDate < dateNow;
      if (isExprired) {
        localStorage.removeItem(StorageKeyEnum.User);
        this.router.navigate(["/account/login"]);
      }
    }
  }

  private setThirdPartyUsernames(userInfo: LoginModel) {
    if (userInfo && !userInfo.isAdmin) {
      (<any>window).analytics.identify(userInfo.userId, {
        name: userInfo.fullName || userInfo.username,
        email: userInfo.email,
        isPriceYard: userInfo.isPriceYard,
        isShipyard: userInfo.isShipyard,
        isYardener: userInfo.isYardener,
        isPlanyard: userInfo.isPlanYard,
        isNetyard: userInfo.isNetYard,
        company: userInfo.company,
        phone: userInfo.phone,
      });

      (<any>window).Intercom("boot", {
        app_id: "mf0ps7e6",
        name: userInfo.fullName || userInfo.username,
        email: userInfo.email,
        user_id: userInfo.userId,
        tenantName:userInfo.tenantName
      });

      (<any>window).Canny('identify', {
        appID: '61a64b6bff36736747deecb6',
        user: {
          email: userInfo.email,
          name: userInfo.fullName || userInfo.username,
          id: userInfo.userId
        },
      });
      mixpanel.init('f9bf95173a2d308c1272494ce39b5bf8', { debug: true, ignore_dnt: true });
      mixpanel.identify(userInfo.userId.toString());
      mixpanel.people.set(userInfo);
    } else if (userInfo && userInfo.isAdmin) {
      (<any>window).Intercom("shutdown");
    }
  }

  setRouteByPermission(event){
    if(event.snapshot.data.permissionName && this.userInfo.role !== 'Admin' && event.snapshot.data.permissionName !== 'Dashboard'){
      const definedPermision=PermissionsEnum[event.snapshot.data.permissionName];
      const index= this.userInfo?.rolePermission?.findIndex((res:any)=>res === PermissionsEnum[event.snapshot.data.permissionName]);
      if(index == -1 && definedPermision) this.router.navigate(["/dashboard/product/netyard"]);
    }
  }

  logReport(){
    if(this.router.routerState.snapshot.url?.split('/')[2] !== 'kpi' && this.parentRouteName == 'analytics' && this.userInfo){
        this.inventoryService.SaveReportLogEvent(this.userInfo.tenantId).pipe(takeUntil(this.destroy$)).subscribe((res)=>{});
    }
  }
}
